import React, { useEffect, useRef, useState } from "react";
import { MdCancel, MdOutlineCloudUpload } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import ButtonLoading from "../Button loading/ButtonLoading";
import { toast } from "react-toastify";
import { showConfirmDialog } from "../showConfirmDialog/ShowConfirmDialog";
import { showErrorToast } from "../../utils/Utils";
import { useAddPartnerMutation, useDeletePartnerMutation, useGetPartnerByIdQuery, useUpdatePartnerMutation } from "../../redux/partnerApi";

const AddPartner = () => {
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    link: "",
  });

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const { data, isLoading: isPartnerLoading } = useGetPartnerByIdQuery(id, {
    refetchOnFocus: false,
    skip: !isEditMode,
  });

  useEffect(() => {
    if (isEditMode && data) {
      setFormData({
        name: data?.partner?.name,
        link: data?.partner?.link,
      });

      if (data?.partner?.icon?.imageUrl) {
        setImage({
          src: data?.partner?.icon?.imageUrl,
          file: null,
        });
      }
    }
  }, [isEditMode, data]);

  const [addPartner, { isLoading: isAddLoading }] =
    useAddPartnerMutation();
  const [updatePartner, { isLoading: isUpdateLoading }] =
    useUpdatePartnerMutation();
  const [deletePartner, { isLoading: isDeleteLoading }] =
    useDeletePartnerMutation();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    setError("");

    if (!file) {
      setError("No file selected.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
     const newImage = {
        src: objectUrl,
        file,
      };

      setImage(newImage);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = () => {
    setImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!image) {
      toast.error("Please upload an image.");
      setError("Please upload an image.");
      return;
    }

    const bannerData = new FormData();
    bannerData.append("name", formData.name);
    bannerData.append("link", formData.link);
    bannerData.append("partnerIcon", image?.file);

    try {
      let response;
      if (isEditMode) {
        response = await updatePartner({ id, partnerData: bannerData }).unwrap();
      } else {
        response = await addPartner(bannerData).unwrap();
      }
      if (response.success) {
        handleCancel();
        toast.success(response.message);
        navigate("/partners");
      }
    } catch (err) {
      showErrorToast(err);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      link: "",
    });
    setImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeletePartner = async (id) => {
    try {
      const response = await deletePartner(id).unwrap();
      if (response.success) {
        navigate("/partners");
        toast.success(response.message);
      }
    } catch (err) {
      console.error("partner error:", err);
      showErrorToast(err);
    }
  };

  const handleDeleteConfrim = (id) => {
    showConfirmDialog({
      title: "Delete Partner",
      message: "Are you sure you want to delete this partner?",
      type: "danger",
      onConfirm: () => {
        handleDeletePartner(id);
      },
    });
  };

  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
        {isEditMode ? "Update Partner" : "Create Partner"}
      </h1>
      {isPartnerLoading ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
          </div>
          <div className="flex flex-col gap-1 w-[50%]">
            <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
          </div>
          <div className="flex flex-col gap-1 ">
            <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            <div className=" w-full h-24 rounded-xl bg-[#efefef] animate-pulse"></div>
          </div>
          <div className="flex items-center justify-end gap-4">
            <div className="md:w-[9rem] w-[4rem]   rounded-xl md:h-14 h-6 bg-[#efefef] animate-pulse"></div>
            <div className="md:w-[9rem] w-[4rem]   rounded-xl md:h-14 h-6 bg-[#efefef] animate-pulse"></div>
            <div className="md:w-[9rem] w-[4rem]   rounded-xl md:h-14 h-6 bg-[#efefef] animate-pulse"></div>
          </div>
        </div>
      ) : (
        <form
          className="flex flex-col lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem]"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col md:gap-1 gap-[2px] lg:w-[50%] md:w-[70%] w-full">
            <label className="font-semibold">Name</label>
            <input
              type="text"
              placeholder="Partner name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
            />
          </div>
          <div className="flex flex-col md:gap-1 gap-[2px] lg:w-[50%] md:w-[70%] w-full">
            <label className="font-semibold">Link</label>
            <input
              type="text"
              placeholder="https://rda.gop.pk/"
              name="link"
              value={formData.link}
              onChange={handleChange}
              className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
            />
          </div>
          
          <div className="flex flex-col md:gap-1 gap-[2px]">
            <label className="font-semibold">Partner Icon</label>
            <div
              className="w-full border border-[#00000033] md:p-4 p-3 rounded-xl "
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <label
                htmlFor="file-upload"
                className="cursor-pointer  flex flex-col md:gap-2 gap-1 items-center w-full "
              >
                <MdOutlineCloudUpload className="text-[#00643b] lg:size-10 md:size-8 size-6" />
                <p className="text-center font-semibold text-[#0B0B0B] ">
                  Drag your file(s) or{" "}
                  <span className=" text-[#00643b]   ">browse</span>
                </p>

                <p className="text-[#6D6D6D] text-center md:text-sm text-[12px]">
                  Max 10 MB files are allowed
                </p>
                {/* <p className="text-[#6D6D6D] md:text-sm text-[12px] text-center">
                  Banner dimensions should be 1610 x 650 pixels
                </p> */}
              </label>

              <input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                ref={fileInputRef}
              />
              {image && (
                <div className="flex justify-center text-[#6D6D6D] items-center md:gap-4 gap-3 md:text-sm text-[12px] lg:py-[1.3rem] md:py-[1rem] py-[.5rem]">
                  <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                  OR
                  <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                </div>
              )}

              {image && (
                <div className="relative ">
                  <img
                    src={image.src}
                    alt="Upload Preview"
                    className=" object-cover md:rounded-[1rem] rounded-lg w-full "
                  />
                  <button
                    onClick={() => handleDelete()}
                    className="absolute md:-top-2.5 md:-right-2.5 -top-1.5 -right-1.5 bg-white rounded-full  "
                  >
                    <MdCancel className="lg:size-10 md:size-8 size-6 text-red-500" />
                  </button>
                </div>
              )}
            </div>
            {error && (
              <p className="text-red-500 md:text-sm text-[12px]">{error}</p>
            )}
          </div>
          <div className="flex items-center justify-end lg:gap-4 md:gap-3 gap-2">
            <p
              onClick={() => handleCancel()}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border  border-[#00643B] text-[#00643B] text-center cursor-pointer  lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              Cancel
            </p>

            {data || isEditMode ? (
              <div
                disabled={isDeleteLoading}
                onClick={() => handleDeleteConfrim(data?.partner?._id)}
                className={`lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border text-center border-[#D80000] flex items-center justify-center ${
                  isDeleteLoading
                    ? "opacity-50 cursor-not-allowed"
                    : "opacity-100 cursor-pointer"
                } text-white bg-[#D80000] lg:rounded-xl md:rounded-lg rounded-md font-medium`}
              >
                {isDeleteLoading ? (
                  <ButtonLoading text=" Delete..." />
                ) : (
                  "Delete"
                )}
              </div> 
            ) : (
              ""
            )}
            <button
              type="submit"
              disabled={isAddLoading || isUpdateLoading ? true : false}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              {isEditMode ? (
                isUpdateLoading ? (
                  <ButtonLoading text="Update..." />
                ) : (
                  "Update"
                )
              ) : isAddLoading ? (
                <ButtonLoading text="Adding..." />
              ) : (
                "Add Partner"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddPartner;
